import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Check, Sparkles, Crown, Zap, X, Star, Shield, BarChart3, Calendar, Wand2, Diamond, Home, Bell, Users, User, BadgeCheck, Building2, Clock, Gift, Percent } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Pricing = ({ user }) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [annualPlans, setAnnualPlans] = useState([]);
  const [promotion, setPromotion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/subscriptions/plans`);
      setPlans(response.data.plans);
      setAnnualPlans(response.data.annual_plans || []);
      setPromotion(response.data.promotion);
      
      // Default to annual if promo is active
      if (response.data.promotion?.active) {
        setBillingCycle('annual');
      }
    } catch (error) {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const selectPlan = async (tier, isAnnual = false) => {
    const packageId = isAnnual ? `${tier}_annual` : tier;
    setProcessing(packageId);
    
    try {
      // For basic (free) plan, just update directly
      if (tier === 'basic') {
        await axios.post(
          `${API_URL}/subscriptions/update`,
          { tier },
          { withCredentials: true }
        );
        toast.success('You are now on the Basic plan');
        navigate('/dashboard');
        return;
      }
      
      // For paid plans, create checkout session
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API_URL}/subscriptions/checkout`,
        { package_id: packageId, origin_url: originUrl },
        { withCredentials: true }
      );
      
      if (response.data.free) {
        toast.success('Plan updated successfully!');
        navigate('/dashboard');
      } else if (response.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.info('Please log in to continue');
        navigate('/login');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to process plan selection');
      }
    } finally {
      setProcessing(null);
    }
  };

  // Get annual price for a tier
  const getAnnualPrice = (tier) => {
    return annualPlans.find(p => p.tier === tier);
  };

  // Custom Diamond Icon component for Supremium
  const DiamondIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M12 2L2 9L12 22L22 9L12 2Z" />
    </svg>
  );

  const getIcon = (tier) => {
    switch(tier) {
      case 'basic': return User;
      case 'pro': return BadgeCheck;
      case 'enterprise': return Building2;
      case 'supremium': return DiamondIcon;
      case 'enterprise_supremium': return Crown;
      default: return Star;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900" data-testid="pricing-page">
      <nav className="px-6 md:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-heading font-black text-gray-900 dark:text-white">Socyy</h1>
        </Link>
        <div className="flex items-center gap-4">
          {user ? (
            <Link to="/dashboard">
              <Button variant="outline" className="rounded-full font-bold">
                Dashboard
              </Button>
            </Link>
          ) : (
            <Link to="/login">
              <Button variant="ghost" className="rounded-full font-bold">
                Login
              </Button>
            </Link>
          )}
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 pt-8 pb-20">
        {/* Promo Banner */}
        {promotion?.active && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl text-white text-center shadow-lg"
          >
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Gift className="w-6 h-6" />
              <span className="text-xl font-bold">🎉 {promotion.name || 'Welcome Plan'}: 50% OFF All Annual Plans!</span>
              <div className="flex items-center gap-2 bg-white/20 rounded-full px-3 py-1">
                <Clock className="w-4 h-4" />
                <span className="font-semibold">{promotion.days_remaining} days left</span>
              </div>
            </div>
            <p className="text-sm mt-2 opacity-90">Ends June 1st, 2026 - Lock in your Welcome Plan savings now!</p>
          </motion.div>
        )}
        
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-6xl font-heading font-black tracking-tighter mb-4 text-gray-900 dark:text-white" data-testid="pricing-title">
            Supercharge Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">Social Presence</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-8">
            Choose the plan that fits your needs. Upgrade anytime, cancel anytime.
          </p>
          
          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-full font-bold transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2 rounded-full font-bold transition-all relative ${
                billingCycle === 'annual'
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
              }`}
            >
              Annual
              {promotion?.active && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                  50% OFF
                </span>
              )}
            </button>
          </div>
          
          {/* Current Plan Badge */}
          {user && (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-full shadow-lg border border-gray-200 dark:border-gray-700 mb-8">
              <span className="text-sm text-gray-600 dark:text-gray-300">Current Plan:</span>
              <span className="font-bold text-primary capitalize">{user.subscription_tier || 'Basic'}</span>
              {user.is_premium && <Crown className="w-4 h-4 text-yellow-500" />}
            </div>
          )}
        </motion.div>

        {/* Premium Features Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { icon: BarChart3, label: 'Analytics', desc: 'Track your growth' },
            { icon: Calendar, label: 'Calendar', desc: 'Schedule your life' },
            { icon: Home, label: 'Account Hub', desc: 'Manage 5 accounts' },
            { icon: Diamond, label: 'VIP Access', desc: 'Exclusive perks' },
          ].map((feature, idx) => (
            <motion.div
              key={feature.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="p-4 bg-white/50 dark:bg-gray-800/50 rounded-2xl text-center"
            >
              <feature.icon className="w-8 h-8 text-primary mx-auto mb-2" />
              <h3 className="font-bold text-gray-900 dark:text-white">{feature.label}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{feature.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 max-w-7xl mx-auto">
          {plans.map((plan, idx) => {
            const isPro = plan.tier === 'pro';
            const isEnterprise = plan.tier === 'enterprise';
            const isSupremium = plan.tier === 'supremium';
            const isEnterpriseSupremium = plan.tier === 'enterprise_supremium';
            const isCurrent = user?.subscription_tier === plan.tier;
            const Icon = getIcon(plan.tier);
            
            return (
              <motion.div
                key={plan.tier}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className={isEnterpriseSupremium ? 'lg:-mt-4 lg:mb-4' : ''}
              >
                <Card
                  className={`p-5 rounded-3xl border shadow-xl hover:shadow-2xl transition-all relative overflow-hidden ${
                    isCurrent
                      ? 'ring-4 ring-green-400 border-green-300'
                      : isEnterpriseSupremium
                      ? 'border-purple-400 bg-gradient-to-br from-purple-50 via-white to-pink-50 dark:from-gray-800 dark:via-purple-900/20 dark:to-pink-900/20 ring-2 ring-purple-300'
                      : isSupremium
                      ? 'border-yellow-400 bg-gradient-to-br from-yellow-50 via-white to-amber-50 dark:from-gray-800 dark:via-yellow-900/20 dark:to-amber-900/20 ring-2 ring-yellow-300'
                      : isPro
                      ? 'border-blue-300 bg-gradient-to-br from-white to-blue-50 dark:from-gray-800 dark:to-blue-900/30 ring-2 ring-blue-200'
                      : isEnterprise
                      ? 'border-emerald-300 bg-gradient-to-br from-white to-emerald-50 dark:from-gray-800 dark:to-emerald-900/30'
                      : 'border-white/20 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md'
                  }`}
                  data-testid={`plan-${plan.tier}`}
                >
                  {isEnterpriseSupremium && !isCurrent && (
                    <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-bl-2xl flex items-center gap-1">
                      <Crown className="w-3 h-3" /> ULTIMATE
                    </div>
                  )}
                  {isSupremium && !isCurrent && (
                    <div className="absolute top-0 right-0 bg-gradient-to-r from-yellow-500 to-amber-500 text-white text-xs font-bold px-3 py-1 rounded-bl-2xl flex items-center gap-1">
                      <Diamond className="w-3 h-3" /> SUPREMIUM
                    </div>
                  )}
                  {isPro && !isCurrent && (
                    <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-bold px-3 py-1 rounded-bl-2xl">
                      POPULAR
                    </div>
                  )}
                  {isCurrent && (
                    <div className="absolute top-0 right-0 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-bl-2xl">
                      CURRENT
                    </div>
                  )}
                  
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        isEnterpriseSupremium
                          ? 'bg-gradient-to-br from-purple-500 to-pink-500'
                          : isSupremium
                          ? 'bg-gradient-to-br from-yellow-400 to-amber-500'
                          : isPro 
                          ? 'bg-gradient-to-br from-blue-500 to-cyan-500'
                          : isEnterprise
                          ? 'bg-gradient-to-br from-emerald-500 to-teal-500'
                          : 'bg-gray-200 dark:bg-gray-700'
                      }`}>
                        <Icon className={`w-5 h-5 ${isEnterpriseSupremium || isSupremium || isPro || isEnterprise ? 'text-white' : 'text-gray-600 dark:text-gray-300'}`} />
                      </div>
                      <h3 className="text-lg font-heading font-bold text-gray-900 dark:text-white">{plan.name}</h3>
                    </div>
                    
                    {/* Pricing based on billing cycle */}
                    {billingCycle === 'annual' && plan.price > 0 && getAnnualPrice(plan.tier) ? (
                      <div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl md:text-3xl font-heading font-black text-gray-900 dark:text-white">
                            ${getAnnualPrice(plan.tier).monthly_if_annual.toFixed(2)}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 text-sm">/mo</span>
                          <span className="text-red-500 line-through text-sm">${plan.price}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-bold px-2 py-0.5 rounded-full">
                            Save ${getAnnualPrice(plan.tier).total_savings.toFixed(0)}/yr
                          </span>
                          {promotion?.active && (
                            <span className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                              50% OFF
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          ${getAnnualPrice(plan.tier).final_price.toFixed(2)} billed annually
                        </p>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl md:text-3xl font-heading font-black text-gray-900 dark:text-white">
                            ${plan.price}
                          </span>
                          {plan.price > 0 && <span className="text-gray-600 dark:text-gray-400 text-sm">/mo</span>}
                        </div>
                        {plan.price > 0 && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {isEnterpriseSupremium ? 'Ultimate business solution' : isSupremium ? 'All features unlocked' : 'Billed monthly'}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <ul className="space-y-1.5 mb-6 max-h-48 overflow-y-auto">
                    {plan.features.slice(0, 8).map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                          isEnterpriseSupremium ? 'text-purple-500' : isSupremium ? 'text-yellow-500' : isPro ? 'text-blue-500' : isEnterprise ? 'text-emerald-500' : 'text-green-500'
                        }`} />
                        <span className="text-gray-700 dark:text-gray-300 text-xs">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => selectPlan(plan.tier, billingCycle === 'annual' && plan.price > 0)}
                    disabled={processing === plan.tier || processing === `${plan.tier}_annual` || isCurrent}
                    className={`w-full rounded-full font-bold h-10 shadow-lg transition-all text-sm ${
                      isCurrent
                        ? 'bg-green-100 text-green-700 cursor-default hover:bg-green-100'
                        : isEnterpriseSupremium
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:scale-105'
                        : isSupremium
                        ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white hover:scale-105'
                        : isPro
                        ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:scale-105'
                        : isEnterprise
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:scale-105'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-white border-2 border-gray-300 dark:border-gray-600 hover:bg-gray-100'
                    }`}
                    data-testid={`select-${plan.tier}-btn`}
                  >
                    {(processing === plan.tier || processing === `${plan.tier}_annual`) ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Processing...
                      </span>
                    ) : isCurrent ? (
                      'Current'
                    ) : plan.price === 0 ? (
                      'Get Started'
                    ) : isEnterpriseSupremium ? (
                      'Go Ultimate'
                    ) : isSupremium ? (
                      'Go Supremium'
                    ) : (
                      'Upgrade Now'
                    )}
                  </Button>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* FAQ/Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-16"
        >
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            All plans include unlimited posts, direct messaging, and community access
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            Secure payments powered by Stripe. 30-day money-back guarantee.
          </p>
        </motion.div>

        {/* Non-Profit Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-20 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-pink-100 dark:bg-pink-900/30 rounded-full mb-6">
            <span className="text-2xl">🎗️</span>
            <span className="font-bold text-pink-600 dark:text-pink-400">Non-Profit Organizations</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4 text-gray-900 dark:text-white">
            Special Pricing for <span className="text-pink-500">501(c)(3)</span> Organizations
          </h2>
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-8">
            We believe in supporting organizations that make a difference. Verified non-profits receive 50% off all premium tiers.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {[
              { name: 'Non-Profit Basic', price: 'Free', original: 'Free', features: ['Non-Profit Badge', 'Free Locale promotion', 'Donation links'] },
              { name: 'Non-Profit Premium', price: '$4.99', original: '$9.99', features: ['50% off Premium', 'Fundraising tools', 'Volunteer coordination'] },
              { name: 'Non-Profit Business', price: '$14.99', original: '$29.99', features: ['50% off Business', 'Multi-chapter mgmt', 'Impact reporting'] },
              { name: 'Non-Profit Supremium', price: '$24.99', original: '$49.00', features: ['50% off Supremium', 'All premium features', 'Partnership opportunities'] }
            ].map((plan, idx) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 + idx * 0.1 }}
              >
                <Card className="p-5 rounded-2xl border border-pink-200 dark:border-pink-800 bg-gradient-to-br from-pink-50 to-white dark:from-gray-800 dark:to-pink-900/20">
                  <div className="text-center mb-4">
                    <span className="text-3xl mb-2 block">🎗️</span>
                    <h3 className="font-bold text-gray-900 dark:text-white">{plan.name}</h3>
                    <div className="flex items-center justify-center gap-2 mt-2">
                      <span className="text-2xl font-black text-pink-600">{plan.price}</span>
                      {plan.original !== 'Free' && (
                        <span className="text-sm text-gray-400 line-through">{plan.original}</span>
                      )}
                    </div>
                    {plan.original !== 'Free' && (
                      <span className="text-xs bg-pink-500 text-white px-2 py-0.5 rounded-full">50% OFF</span>
                    )}
                  </div>
                  <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-pink-500" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </Card>
              </motion.div>
            ))}
          </div>
          
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-6">
            Requires valid 501(c)(3) determination letter verification. <Link to="/verify/nonprofit" className="text-pink-500 hover:underline">Apply for Non-Profit pricing →</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Pricing;
